import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  HiOutlineArrowLeft,
  HiOutlineInformationCircle,
  HiOutlineLocationMarker,
  HiOutlineTemplate,
  HiOutlineSparkles,
  HiOutlineCurrencyDollar,
  HiOutlineChartBar,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineExternalLink,
  HiOutlineViewGrid,
  HiOutlineShare,
} from 'react-icons/hi'
import projectMindmapService from '../services/projectMindmapService'
import ProjectMindmapView from '../components/mindmap/ProjectMindmapView'

/**
 * ProjectMindmapDetail - View mindmap for a single project
 * Shows 6 branches with expandable content
 * Desktop: 2 columns, clicking a card expands entire row
 * Mobile: Single column accordion
 */
export default function ProjectMindmapDetail() {
  const { slug } = useParams()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('card') // 'card' or 'mindmap'
  const [expandedBranch, setExpandedBranch] = useState(null) // For mobile
  const [expandedRow, setExpandedRow] = useState(null) // For desktop (0, 1, 2 = row index)

  // Branch pairs for desktop rows
  // Row 0: overview + location
  // Row 1: design + amenities
  // Row 2: pricing + progress
  const branchRows = [
    ['overview', 'location'],
    ['design', 'amenities'],
    ['pricing', 'progress']
  ]

  useEffect(() => {
    loadProject()
  }, [slug])

  const loadProject = async () => {
    setLoading(true)
    const result = await projectMindmapService.getProjectBySlug(slug)
    if (result.status === 'success' && result.data) {
      setProject(result.data)
      // Auto-expand first row/branch with content
      const branches = result.data.branches

      // For mobile: expand first branch with content
      for (const key of ['overview', 'location', 'design', 'amenities', 'pricing', 'progress']) {
        if (branches[key]?.items?.length > 0) {
          setExpandedBranch(key)
          break
        }
      }

      // For desktop: expand first row with content
      for (let i = 0; i < branchRows.length; i++) {
        const [left, right] = branchRows[i]
        if (branches[left]?.items?.length > 0 || branches[right]?.items?.length > 0) {
          setExpandedRow(i)
          break
        }
      }
    }
    setLoading(false)
  }

  // Mobile: toggle single branch
  const toggleBranch = (key) => {
    setExpandedBranch(expandedBranch === key ? null : key)
  }

  // Desktop: toggle entire row
  const toggleRow = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex)
  }

  // Branch icons mapping
  const branchIcons = {
    overview: HiOutlineInformationCircle,
    location: HiOutlineLocationMarker,
    design: HiOutlineTemplate,
    amenities: HiOutlineSparkles,
    pricing: HiOutlineCurrencyDollar,
    progress: HiOutlineChartBar
  }

  // Branch colors
  const branchColors = {
    overview: 'bg-blue-500',
    location: 'bg-green-500',
    design: 'bg-purple-500',
    amenities: 'bg-amber-500',
    pricing: 'bg-red-500',
    progress: 'bg-cyan-500'
  }

  const branchLightColors = {
    overview: 'bg-blue-50 border-blue-200',
    location: 'bg-green-50 border-green-200',
    design: 'bg-purple-50 border-purple-200',
    amenities: 'bg-amber-50 border-amber-200',
    pricing: 'bg-red-50 border-red-200',
    progress: 'bg-cyan-50 border-cyan-200'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-500 mb-4">Không tìm thấy dự án</p>
        <Link to="/projects" className="text-primary-600 hover:underline">
          Quay lại danh sách
        </Link>
      </div>
    )
  }

  const branches = project.branches || {}
  const branchKeys = ['overview', 'location', 'design', 'amenities', 'pricing', 'progress']

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/projects"
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <HiOutlineArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {project.name}
              </h1>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                  ${viewMode === 'card'
                    ? 'bg-white shadow-sm text-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
                title="Xem dạng thẻ"
              >
                <HiOutlineViewGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Thẻ</span>
              </button>
              <button
                onClick={() => setViewMode('mindmap')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                  ${viewMode === 'mindmap'
                    ? 'bg-white shadow-sm text-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
                title="Xem dạng sơ đồ"
              >
                <HiOutlineShare className="w-4 h-4" />
                <span className="hidden sm:inline">Sơ đồ</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mindmap View */}
      {viewMode === 'mindmap' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ProjectMindmapView project={project} />
        </div>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <>
          {/* Project Image */}
          {project.avatar_url && (
            <div className="bg-white border-b">
              <div className="max-w-3xl mx-auto">
                <div className="h-48 md:h-64 bg-gray-100">
                  <img
                    src={project.avatar_url}
                    alt={project.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mindmap Branches */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Desktop: Grid Layout with row-based expansion */}
            <div className="hidden md:block space-y-4">
              {branchRows.map((rowKeys, rowIndex) => {
                const isRowExpanded = expandedRow === rowIndex
                const [leftKey, rightKey] = rowKeys
                const leftBranch = branches[leftKey]
                const rightBranch = branches[rightKey]
                const LeftIcon = branchIcons[leftKey]
                const RightIcon = branchIcons[rightKey]
                const leftHasContent = leftBranch?.items?.length > 0
                const rightHasContent = rightBranch?.items?.length > 0
                const rowHasContent = leftHasContent || rightHasContent

                return (
                  <div key={rowIndex} className="space-y-2">
                    {/* Row Header - click to toggle */}
                    <div
                      className={`
                        grid grid-cols-2 gap-4 cursor-pointer
                        ${!rowHasContent ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      onClick={rowHasContent ? () => toggleRow(rowIndex) : undefined}
                    >
                      {/* Left Card Header */}
                      <div className={`
                        rounded-xl border overflow-hidden transition-all
                        ${isRowExpanded ? branchLightColors[leftKey] : 'bg-white border-gray-200'}
                        ${rowHasContent ? 'hover:shadow-md' : ''}
                      `}>
                        <div className="p-4 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${branchColors[leftKey]} flex items-center justify-center flex-shrink-0`}>
                            <LeftIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{leftBranch?.label}</h3>
                            <p className="text-sm text-gray-500">
                              {leftHasContent ? `${leftBranch.items.length} mục` : 'Chưa có thông tin'}
                            </p>
                          </div>
                          {rowHasContent && (
                            isRowExpanded ? (
                              <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
                            )
                          )}
                        </div>
                      </div>

                      {/* Right Card Header */}
                      <div className={`
                        rounded-xl border overflow-hidden transition-all
                        ${isRowExpanded ? branchLightColors[rightKey] : 'bg-white border-gray-200'}
                        ${rowHasContent ? 'hover:shadow-md' : ''}
                      `}>
                        <div className="p-4 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${branchColors[rightKey]} flex items-center justify-center flex-shrink-0`}>
                            <RightIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{rightBranch?.label}</h3>
                            <p className="text-sm text-gray-500">
                              {rightHasContent ? `${rightBranch.items.length} mục` : 'Chưa có thông tin'}
                            </p>
                          </div>
                          {rowHasContent && (
                            isRowExpanded ? (
                              <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row Content - expanded content for both cards */}
                    {isRowExpanded && rowHasContent && (
                      <div className="grid grid-cols-2 gap-4">
                        {/* Left Content */}
                        <div className={`rounded-xl border ${branchLightColors[leftKey]} p-4`}>
                          {leftHasContent ? (
                            <ul className="space-y-2">
                              {leftBranch.items.map((item, idx) => (
                                <BranchItem key={idx} item={item} />
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Chưa có thông tin</p>
                          )}
                        </div>

                        {/* Right Content */}
                        <div className={`rounded-xl border ${branchLightColors[rightKey]} p-4`}>
                          {rightHasContent ? (
                            <ul className="space-y-2">
                              {rightBranch.items.map((item, idx) => (
                                <BranchItem key={idx} item={item} />
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Chưa có thông tin</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Mobile: Accordion Layout */}
            <div className="md:hidden space-y-3">
              {branchKeys.map(key => {
                const branch = branches[key]
                const Icon = branchIcons[key]
                const hasContent = branch?.items?.length > 0

                return (
                  <BranchAccordion
                    key={key}
                    branchKey={key}
                    branch={branch}
                    Icon={Icon}
                    color={branchColors[key]}
                    lightColor={branchLightColors[key]}
                    expanded={expandedBranch === key}
                    onToggle={() => toggleBranch(key)}
                    hasContent={hasContent}
                  />
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * BranchAccordion - Mobile accordion item
 */
function BranchAccordion({ branchKey, branch, Icon, color, lightColor, expanded, onToggle, hasContent }) {
  return (
    <div
      className={`
        rounded-xl border overflow-hidden transition-all
        ${expanded ? lightColor : 'bg-white border-gray-200'}
        ${!hasContent ? 'opacity-50' : ''}
      `}
    >
      {/* Header */}
      <button
        className="w-full p-4 flex items-center gap-3 text-left"
        onClick={hasContent ? onToggle : undefined}
        disabled={!hasContent}
      >
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">{branch?.label}</h3>
          <p className="text-sm text-gray-500">
            {hasContent ? `${branch.items.length} mục` : 'Chưa có thông tin'}
          </p>
        </div>
        {hasContent && (
          expanded ? (
            <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
          )
        )}
      </button>

      {/* Content */}
      {expanded && hasContent && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <ul className="mt-3 space-y-2">
            {branch.items.map((item, idx) => (
              <BranchItem key={idx} item={item} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * BranchItem - Single item in branch
 * Handles special items like __map__ for Google Maps embed
 */
function BranchItem({ item }) {
  // Check for special items
  if (item.startsWith('__map__:')) {
    const mapUrl = item.replace('__map__:', '')
    return (
      <li className="bg-white rounded-lg border overflow-hidden">
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 p-3 text-primary-600 hover:bg-primary-50 transition-colors"
        >
          <HiOutlineLocationMarker className="w-5 h-5" />
          <span>Xem trên Google Maps</span>
          <HiOutlineExternalLink className="w-4 h-4 ml-auto" />
        </a>
      </li>
    )
  }

  return (
    <li className="flex items-start gap-2 text-gray-700">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></span>
      <span className="text-sm leading-relaxed">{item}</span>
    </li>
  )
}
